import React, { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import SoldxHeader from "../../../components/SoldxHeader"
import SoldxPageHero from "../../../components/SoldxPageHero"
import SoldxBreadcrumb from "../../../components/SoldxBreadcrumb"
import AuctionCard, { ApiListing } from "../../../components/AuctionCard"
import apiClient from "helpers/apiClient"
import { navigateToPropertyDetail, type PropertyKycBlockReason } from "helpers/auth_api"
import KycRequiredModal from "../../../components/KycRequiredModal/KycRequiredModal"
import SoldxLandingFooter from "../../../components/SoldxLandingFooter"

const SEARCH_LISTINGS_URL = "/api/listings/search"

// Legacy export for PropertyDetail (uses numeric id from URL; empty since we use API listings on this page)
export const properties: Array<Record<string, unknown>> = []

const Properties = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [listings, setListings] = useState<ApiListing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [categoryInput, setCategoryInput] = useState(searchParams.get("category") || "")
  const [locationInput, setLocationInput] = useState(searchParams.get("location") || "")
  const [addressInput, setAddressInput] = useState(searchParams.get("address") || "")
  const [priceRangeInput, setPriceRangeInput] = useState(searchParams.get("priceRange") || "")
  const [amenitiesInput, setAmenitiesInput] = useState(searchParams.get("amenities") || "")
  const [tagInput, setTagInput] = useState(searchParams.get("tag") || "")
  const [kycModalOpen, setKycModalOpen] = useState(false)
  const [kycModalVariant, setKycModalVariant] = useState<PropertyKycBlockReason>("upload")

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams()
    if (categoryInput.trim()) params.set("category", categoryInput.trim())
    if (locationInput.trim()) params.set("location", locationInput.trim())
    if (addressInput.trim()) params.set("address", addressInput.trim())
    if (priceRangeInput) params.set("priceRange", priceRangeInput)
    if (amenitiesInput.trim()) params.set("amenities", amenitiesInput.trim())
    if (tagInput.trim()) params.set("tag", tagInput.trim())
    setSearchParams(params, { replace: true })
  }

  const handleClearFilters = () => {
    setCategoryInput("")
    setLocationInput("")
    setAddressInput("")
    setPriceRangeInput("")
    setAmenitiesInput("")
    setTagInput("")
    setSearchParams(new URLSearchParams(), { replace: true })
  }

  useEffect(() => {
    setCategoryInput(searchParams.get("category") || "")
    setLocationInput(searchParams.get("location") || "")
    setAddressInput(searchParams.get("address") || "")
    setPriceRangeInput(searchParams.get("priceRange") || "")
    setAmenitiesInput(searchParams.get("amenities") || "")
    setTagInput(searchParams.get("tag") || "")
  }, [searchParams])

  const category = searchParams.get("category") || ""
  const location = searchParams.get("location") || ""
  const address = searchParams.get("address") || ""
  const priceRange = searchParams.get("priceRange") || ""
  const amenities = searchParams.get("amenities") || ""
  const tag = searchParams.get("tag") || ""
  const bathsBeds = searchParams.get("bathsBeds") || ""
  const hasActiveFilters = !!(category || location || address || priceRange || amenities || tag || bathsBeds)

  useEffect(() => {
    setLoading(true)
    setError(null)
    const params: Record<string, string | number> = { limit: 24, page: 1 }
    if (category) params.category = category
    if (location) params.location = location
    if (address) params.address = address
    if (priceRange) params.priceRange = priceRange
    if (amenities) params.amenities = amenities
    if (tag) params.tag = tag
    apiClient
      .get<{ success: boolean; data?: ApiListing[] }>(SEARCH_LISTINGS_URL, { params })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          setListings(res.data.data)
        } else {
          setListings([])
        }
      })
      .catch(() => {
        setError("Failed to load properties.")
        setListings([])
      })
      .finally(() => setLoading(false))
  }, [category, location, address, priceRange, amenities, tag])

  const filteredListings = useMemo(() => {
    if (!bathsBeds) return listings
    const match = bathsBeds.match(/^(\d+)b(\d+)ba$/)
    if (!match) return listings
    const minBeds = parseInt(match[1], 10)
    const minBaths = parseInt(match[2], 10)
    return listings.filter((l) => {
      const beds = l.categorySpecific?.numberOfBedrooms ?? 0
      const baths = l.categorySpecific?.numberOfBathrooms ?? 0
      return beds >= minBeds && baths >= minBaths
    })
  }, [listings, bathsBeds])

  const handleViewDetails = (listing: ApiListing) => {
    navigateToPropertyDetail(navigate, listing._id, {
      onKycRequired: (reason) => {
        setKycModalVariant(reason)
        setKycModalOpen(true)
      },
    })
  }

  return (
    <div className="soldx-landing">
      <SoldxHeader solid />
      <SoldxPageHero
        title="Explore Properties"
        belowTitle={<SoldxBreadcrumb variant="hero" />}
        showArrows
      />

      <section className="properties-search-bar">
        <div className="container">
          <form onSubmit={handleSearchSubmit} className="properties-search-form">
            <div className="properties-search-fields">
              <select
                value={categoryInput}
                onChange={(e) => setCategoryInput(e.target.value)}
                className="properties-search-select"
                title="Property type"
              >
                <option value="">Property Type</option>
                <option value="Commercial Office Space">Commercial Office Space</option>
                <option value="Independent Building (Industrial)">Independent Building (Industrial)</option>
                <option value="Independent Building (Retail)">Independent Building (Retail)</option>
                <option value="Independent Building (Commercial office)">Independent Building (Commercial)</option>
                <option value="Retail Space">Retail Space</option>
                <option value="Warehouse">Warehouse</option>
                <option value="Hospitality">Hospitality</option>
                <option value="Franchise">Franchise</option>
                <option value="Land">Land</option>
              </select>
              <div className="properties-search-field">
                <i className="mdi mdi-map-marker" />
                <input
                  type="text"
                  placeholder="Location (city, state)"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                />
              </div>
              <select
                value={priceRangeInput}
                onChange={(e) => setPriceRangeInput(e.target.value)}
                className="properties-search-select"
              >
                <option value="">Price Range</option>
                <option value="0-1">Under ₹1 Cr</option>
                <option value="1-5">₹1 Cr - ₹5 Cr</option>
                <option value="5-10">₹5 Cr - ₹10 Cr</option>
                <option value="10+">₹10 Cr+</option>
              </select>
              <div className="properties-search-field">
                <i className="mdi mdi-widgets" />
                <input
                  type="text"
                  placeholder="Amenities"
                  value={amenitiesInput}
                  onChange={(e) => setAmenitiesInput(e.target.value)}
                />
              </div>
              <div className="properties-search-field">
                <i className="mdi mdi-tag" />
                <input
                  type="text"
                  placeholder="Tag (e.g. Hot Investment)"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                />
              </div>
              <button type="submit" className="properties-search-btn">
                <i className="mdi mdi-magnify" />
                Search
              </button>
              {hasActiveFilters && (
                <button type="button" className="properties-search-btn properties-search-btn--clear" onClick={handleClearFilters}>
                  <i className="mdi mdi-filter-off" />
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>
      </section>

      <main className="properties-container py-5">
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-2 text-muted">Loading properties...</p>
          </div>
        )}
        {error && (
          <div className="alert alert-warning text-center">
            {error} <a href="/bidding">Go to Bidding</a>
          </div>
        )}
        {!loading && !error && listings.length === 0 && (
          <div className="text-center py-5 text-muted">
            {hasActiveFilters ? (
              <>
                <p className="mb-2">No properties match your search for this price range or filters.</p>
                <p className="mb-3">Try a different price range (e.g. ₹5 Cr – ₹10 Cr) or clear filters to see all listings.</p>
                <Link to="/properties" className="btn btn-primary">View all properties</Link>
              </>
            ) : (
              "No properties available for bidding right now. Check back later."
            )}
          </div>
        )}
        {!loading && !error && listings.length > 0 && filteredListings.length === 0 && (
          <div className="text-center py-5 text-muted">
            No properties match your filters. Try adjusting location, type, price or baths &amp; beds.
          </div>
        )}
        {!loading && !error && filteredListings.length > 0 && (
          <div className="row g-4">
            {filteredListings.map((listing) => (
              <div key={listing._id} className="col-md-6 col-lg-4">
                <AuctionCard listing={listing} onViewDetails={handleViewDetails} />
              </div>
            ))}
          </div>
        )}
      </main>

      <SoldxLandingFooter />
      <KycRequiredModal isOpen={kycModalOpen} variant={kycModalVariant} toggle={() => setKycModalOpen(false)} />
    </div>
  )
}

export default Properties
